import uuid from 'uuid/v4';
import toUpper from 'lodash/toUpper';
import sampleWhiteIcon from '../images/sample-icon_white.png';
import weightedWhiteIcon from '../images/weighted-icon_white.png';
import verticalWhiteIcon from '../images/vertical-icon-down_white.png';
import { numberWithCommas } from './CommonUtils';
import { ONE_MEDIA_VEHICLE, ONE_STATEMENT, ONE_DIMENSION, ONE_STATEMENT_VARIABLE,ONE_MAGAZINE,
  ONE_TELEVISION, ONE_WEBSITE, DESELECT_WEBSITE_MAGAZINE,DESELECT_TV_MAGAZINE,
  DESELECT_TV_WEBSITE, 
  DESELECT_GA_STATEMENT,
  DESELECT_NO_AD_STREAMING } from '../components/mme/MMEConstants';

export const DEFAULT_MME_DATA_KEYS = {
  Study_UNIVERSE: 'StudyUniverseKey',
};

export const SORT_DIRECTION = {
  NONE: 'none',
  ASC: 'asc',
  DESC: 'desc',
};

const stripToNumber = val => Number(val.replace(/[%*, ]/g, ''));

const upperCaseIfString = val =>
  typeof val === 'string' || val instanceof String ? val.toUpperCase() : val;

export const DEFAULT_FIRST_HEADER_CELL_ELEMENTS = [
  {
    key: 'rowHeader',
    isVisible: true,
    isTrendVisibility: true,
    label: 'MEDIA VEHICLE',
    bgColorClass: 'sampleIcon',
    iconImage: sampleWhiteIcon,
    compute: upperCaseIfString,
    text: 'MEDIA VEHICLE',
    widthClass: 'mme-cell-vehicle',
  },
  {
    key: 'observation',
    isVisible: true,
    isTrendVisibility: true,
    label: 'OBSERVATION',
    bgColorClass: 'sampleIcon',
    iconImage: sampleWhiteIcon,
    compute: stripToNumber,
    text: 'OBSERVATION',
    widthClass: 'mme-cell-obs',
  },
];

export const DEFAULT_CELL_ELEMENTS = [
  {
    key: 'p',
    isVisible: true,
    isTrendVisibility: true,
    label: 'PERCENT',
    bgColorClass: 'sampleIcon',
    iconImage: sampleWhiteIcon,
    compute: undefined,
    text: 'PERCENT',
    iconClass: 'sample-cell-icon',
  },
  {
    key: 'i',
    isVisible: true,
    isTrendVisibility: true,
    label: 'INDEX',
    bgColorClass: 'weightedIcon',
    iconImage: weightedWhiteIcon,
    compute: undefined,
    text: 'INDEX',
    iconClass: 'sample-cell-icon',
  },
  {
    key: 'r',
    isVisible: true,
    isTrendVisibility: true,
    label: 'RANK',
    bgColorClass: 'verticalIcon',
    iconImage: verticalWhiteIcon,
    compute: undefined,
    text: 'RANK',
    iconClass: 'sample-cell-icon',
  },
];

export const DEFAULT_DCR_CELL_ELEMENTS = [
  {
    key: 's0',
    isVisible: true,
    label: '100-199',
    compute: undefined,
    text: '100-199',
  },
  {
    key: 's1',
    isVisible: true,
    label: '200-299',
    compute: undefined,
    text: '200-299',
  },
  {
    key: 's2',
    isVisible: true,
    label: '300-399',
    compute: undefined,
    text: '300-399',
  },
  {
    key: 's3',
    isVisible: true,
    label: '400-500',
    compute: undefined,
    text: '400-500',
  },
];

export const DEFAULT_DR_CELL_ELEMENTS = [
  {
    key: 's',
    isVisible: true,
    label: 'SCORE',
    compute: undefined,
    text: 'SCORE',
  },
  {
    key: 'i',
    isVisible: true,
    label: 'INDEX',
    compute: undefined,
    text: 'INDEX',
  },
  {
    key: 'r',
    isVisible: true,
    label: 'RANK',
    compute: undefined,
    text: 'RANK',
  },
];

const customMMEComparator = (
  sortColumnIndex,
  sortKey,
  sortOrder,
  primer,
) => {
  const getCellMetrics = primer
    ? row => primer(row.cellList[sortColumnIndex][sortKey])
    : row => row.cellList[sortColumnIndex][sortKey];

  const getCellMetricsFirstHeader = primer
  ? row => primer(row[sortKey])
  : row => row[sortKey];

  const sortOrderNumber = sortOrder === SORT_DIRECTION.ASC ? 1 : -1;

  return (a, b) => {
    const el1 = sortKey === 'rowHeader' || sortKey === 'observation' ? getCellMetricsFirstHeader(a) : getCellMetrics(a);
    const el2 = sortKey === 'rowHeader' || sortKey === 'observation' ? getCellMetricsFirstHeader(b) : getCellMetrics(b);
    return sortOrderNumber * ((el1 === el2) ? (a.rowHeader > b.rowHeader ? sortOrderNumber : -sortOrderNumber)
      : (el1 > el2) - (el2 > el1));
  };
};

export const sortMMETable = (mmeTable, type) => {
  const sortColumnIndex = mmeTable.sortColumnIndex;
  const sortKey = mmeTable.sortKey;
  const sortOrder = mmeTable.sortOrder;
  let cellElements = '';
  if (sortKey === 'rowHeader' || sortKey === 'observation') {
    cellElements = DEFAULT_FIRST_HEADER_CELL_ELEMENTS;
  } else {
    cellElements = DEFAULT_CELL_ELEMENTS;
    if (type === 'DCR') {
      cellElements = DEFAULT_DCR_CELL_ELEMENTS;
    } else if (type === 'DR') {
      cellElements = DEFAULT_DR_CELL_ELEMENTS;
    }
  }
  const primer = cellElements.filter(
    cellElement => cellElement.key === sortKey,
  )[0].compute;

  const newRowSubList = mmeTable.crosstabPerStudy[0][
    mmeTable.selectedBase
  ].rowList.slice(1);

  const rowList = [
    mmeTable.crosstabPerStudy[0][mmeTable.selectedBase].rowList[0],
    ...newRowSubList.sort(
      customMMEComparator(sortColumnIndex, sortKey, sortOrder, primer),
    ),
  ];

  const mmeAtBaseStudy = mmeTable.crosstabPerStudy[0].map(
    (mme, index) => {
      if (index === mmeTable.selectedBase) {
        return {
          ...mme,
          rowList,
        };
      }
      return mme;
    },
  );

  return {
    ...mmeTable,
    crosstabPerStudy: [
      mmeAtBaseStudy,
      ...mmeTable.crosstabPerStudy.slice(1),
    ],
  };
};

export const smartRoundOff = value => {
  if (value < 1) {
    return Math.round(value * 100) / 100;
  }

  if (value < 100) {
    return Math.round(value * 10) / 10;
  }

  return Math.round(value);
};

export const getColumnRowColor = value => {
  switch (value) {
    case 0: return { backgroundColor: 'RGB(201, 220, 241)' };
    case 1: return { backgroundColor: 'RGB(203, 160, 117)' };
    case 2: return { backgroundColor: 'RGB(115, 147, 156)' };
    case 3: return { backgroundColor: 'RGB(56, 124, 44)' };
    case 4: return { backgroundColor: 'RGB(149, 156, 112)' };
    case 5: return { backgroundColor: 'RGB(103, 168, 216)' };
    case 6: return { backgroundColor: 'RGB(218, 188, 94)' };
    case 7: return { backgroundColor: 'RGB(139, 118, 141)' };
    case 8: return { backgroundColor: 'RGB(233, 217, 198)' };
    case 9: return { backgroundColor: 'RGB(243, 229, 195)' };
    case 10: return { backgroundColor: 'RGB(197, 207, 140)' };
    case 11: return { backgroundColor: 'RGB(212, 212, 194)' };
    case 12: return { backgroundColor: 'RGB(201, 220, 241)' };
    default: return { backgroundColor: '#30435D' };
  }
};

export const getColumnColor = value => {
  switch (value) {
    case 0: return 'RGB(201, 220, 241)';
    case 1: return 'RGB(203, 160, 117)';
    case 2: return 'RGB(115, 147, 156)';
    case 3: return 'RGB(56, 124, 44)';
    case 4: return 'RGB(149, 156, 112)';
    case 5: return 'RGB(103, 168, 216)';
    case 6: return 'RGB(218, 188, 94)';
    case 7: return 'RGB(139, 118, 141)';
    case 8: return 'RGB(233, 217, 198)';
    case 9: return 'RGB(243, 229, 195)';
    case 10: return 'RGB(197, 207, 140)';
    case 11: return 'RGB(212, 212, 194)';
    case 12: return 'RGB(201, 220, 241)';
    default: return '#30435D';
  }
};

export const getUpdatedScoreValue = scores => {
  let scoreValues = [];
  scoreValues = scores.map(score => {
    return { s0: score.c[0], s1: score.c[1], s2: score.c[2], s3: score.c[3] };
  });
  return scoreValues;
};

export const populateMMEData = (MMEDataParams, reportType) => {
  const {
    mmeData,
  } = MMEDataParams;
  let tableData = {};
  if (mmeData && mmeData.rptItems.length > 0 && mmeData.scoreDescs.length > 0) {
    const isLabelUpdated = false;
    tableData = {
      idKey: uuid(),
      selectedDropdownColumn: 0,
      isActive: true,
      columnHeaderList: mmeData.scoreDescs.map(item => {
        return { label: item.name, colorVal: item.cid };
      }),
      crosstabPerStudy: [],
    };

    const mmeTableData = [];
    mmeTableData.push(mmeData.rptItems);

    const rowList = [];

    mmeData.rptItems.forEach(item => {
      const { targList, vname } = item;
      targList.forEach(target => {
        const { scores, obs } = target;
        if (
          scores &&
          scores.length > 0
        ) {
          let updatedScore;
          updatedScore = scores;
          if (reportType === 'DCR') {
            updatedScore = getUpdatedScoreValue(scores);
          }
          const rowListObj = {
            rowHeader: vname === 'All Rows' ? toUpper(vname) : vname,
            observation: numberWithCommas(obs),
            cellList: updatedScore,
          };
          rowList.push(rowListObj);
        }
      });
    });

    // rowList = mmeData.rptItems.map(
    //   (mmePerStudyData, studyIndex) => {
    //     if (
    //       mmePerStudyData &&
    //       mmePerStudyData.targList &&
    //       mmePerStudyData.targList.length > 0
    //     ) {
    //       rowList = [];
    //       const { targList, vname } = mmePerStudyData;
    //       return targList.map(mme => {
    //         const { obs, scores } = mme;
    //         if (
    //           scores &&
    //           scores.length > 0
    //         ) {
    //           // const baseCell = {
    //           //   observation: obs,
    //           //   index: scores[0].i,
    //           //   rank: scores[0].r,
    //           //   percentage: scores[0].p,
    //           // };

    //           // rowList = scores.map(row => {
    //           //   const computedCell = {
    //           //       // observation: obs,
    //           //     index: row.i,
    //           //     rank: row.r,
    //           //     percentage: row.p,
    //           //   };
    //           //   const cellList = [];
    //           //   cellList.push(computedCell);
    //           //   return {
    //           //     rowHeader: vname, cellList,
    //           //   };
    //           // });
    //           const rowListObj = {
    //             rowHeader: vname,
    //             cellList: scores,
    //           };
    //           rowList.push(rowListObj);
    //         }
    //         return rowList;
    //       });
    //     }
    //     return undefined;
    //   },
    // );

    const perBaseReturnData = {};

    perBaseReturnData.rowList = rowList;
    const perBaseReturntableData = [];
    perBaseReturntableData.push(perBaseReturnData);
    tableData.crosstabPerStudy = [perBaseReturntableData];

    tableData.isLabelUpdated = isLabelUpdated;
    const mmeDefaultStudy = tableData.crosstabPerStudy[0];
    tableData.selectedBase = 0;
    tableData.selectedBaseName = mmeDefaultStudy.filter;
    return tableData;
  }
  return tableData;
};

export const mmeSReportRunValidationMessage = (params, mmeStudy, nonAdVehicleAndAdStatement) => {
  let errMsg = [];
  let isMediaSpecificError = false;
  let isStreamingSpecificError = false;
  const {
    noAdStreaming,
    gAStatement,
  } = nonAdVehicleAndAdStatement;
  if (params) {
    if (params.scoreList && !params.scoreList.length > 0
     && params.statementVariables && !params.statementVariables.length > 0
     && params.vehicleCodes && !params.vehicleCodes.length > 0) {
      errMsg.push(ONE_MEDIA_VEHICLE);
      errMsg.push(ONE_STATEMENT);
      errMsg.push(ONE_STATEMENT_VARIABLE);
    } else if (params.vehicleCodes && !params.vehicleCodes.length > 0
            && params.scoreList && !params.scoreList.length > 0) {
      errMsg.push(ONE_MEDIA_VEHICLE);
      errMsg.push(ONE_STATEMENT);
    } else if (params.vehicleCodes && !params.vehicleCodes.length > 0
            && params.statementVariables && !params.statementVariables.length > 0) {
      errMsg.push(ONE_MEDIA_VEHICLE);
      errMsg.push(ONE_STATEMENT_VARIABLE);
    } else if (params.scoreList && !params.scoreList.length > 0
            && params.statementVariables && !params.statementVariables.length > 0) {
      errMsg.push(ONE_STATEMENT);
      errMsg.push(ONE_STATEMENT_VARIABLE);
    } else if (params.vehicleCodes && !params.vehicleCodes.length > 0) {
      errMsg.push(ONE_MEDIA_VEHICLE);
    } else if (params.scoreList && !params.scoreList.length > 0) {
      errMsg.push(ONE_STATEMENT);
    } else if (params.statementVariables && !params.statementVariables.length > 0) {
      errMsg.push(ONE_STATEMENT_VARIABLE);
    } else if (mmeStudy.ss === "MRI" && noAdStreaming.length && gAStatement.length) {
      errMsg.push(DESELECT_NO_AD_STREAMING);
      errMsg.push(DESELECT_GA_STATEMENT);
      isStreamingSpecificError = true;
    } else if (params.statementNames.indexOf('G') < 0 &&
               params.statementNames.indexOf('M') >= 0 &&
               params.vehicleNames.indexOf('M') < 0 &&
              (params.vehicleNames.indexOf('T') >= 0 || params.vehicleNames.indexOf('I') >= 0)) {
      errMsg.push(DESELECT_TV_WEBSITE);
      errMsg.push(ONE_MAGAZINE);
      isMediaSpecificError = true;
    } else if (params.statementNames.indexOf('G') < 0 &&
               params.statementNames.indexOf('M') >= 0 &&
               params.vehicleNames.indexOf('M') >= 0 &&
              (params.vehicleNames.indexOf('T') >= 0 || params.vehicleNames.indexOf('I') >= 0)) {
      errMsg.push(DESELECT_TV_WEBSITE);
      isMediaSpecificError = true;
    } else if (params.statementNames.indexOf('G') < 0 &&
               params.statementNames.indexOf('I') >= 0 &&
               params.vehicleNames.indexOf('I') < 0 &&
              (params.vehicleNames.indexOf('T') >= 0 || params.vehicleNames.indexOf('M') >= 0)) {
      errMsg.push(DESELECT_TV_MAGAZINE);
      errMsg.push(ONE_WEBSITE);
      isMediaSpecificError = true;
    } else if (params.statementNames.indexOf('G') < 0 &&
               params.statementNames.indexOf('I') >= 0 &&
               params.vehicleNames.indexOf('I') >= 0 &&
              (params.vehicleNames.indexOf('T') >= 0 || params.vehicleNames.indexOf('M') >= 0)) {
      errMsg.push(DESELECT_TV_MAGAZINE);
      isMediaSpecificError = true;
    } else if (params.statementNames.indexOf('G') < 0 &&
               params.statementNames.indexOf('T') >= 0 &&
               params.vehicleNames.indexOf('T') < 0 &&
              (params.vehicleNames.indexOf('I') >= 0 || params.vehicleNames.indexOf('M') >= 0)) {
      errMsg.push(DESELECT_WEBSITE_MAGAZINE);
      errMsg.push(ONE_TELEVISION);
      isMediaSpecificError = true;
    } else if (params.statementNames.indexOf('G') < 0 &&
               params.statementNames.indexOf('T') >= 0 &&
               params.vehicleNames.indexOf('T') >= 0 &&
               (params.vehicleNames.indexOf('I') >= 0 || params.vehicleNames.indexOf('M') >= 0)) {
      errMsg.push(DESELECT_WEBSITE_MAGAZINE);
      isMediaSpecificError = true;
    } else {
      errMsg = [];
    }
  }
  const validationMsg = {};
  validationMsg.errMsg = errMsg;
  validationMsg.isMediaSpecificError = isMediaSpecificError;
  validationMsg.isStreamingSpecificError = isStreamingSpecificError;
  return validationMsg;
};

export const mmeEReportRunValidationMessage = (params, mmeStudy, nonAdVehicleAndAdStatement) => {
  let errMsg = [];
  let isMediaSpecificError = false;
  let isStreamingSpecificError = false;
  const {
    noAdStreaming,
    adStatement,
  } = nonAdVehicleAndAdStatement;
  if (params) {
    if (params.scoreList && !params.scoreList.length > 0
     && params.vehicleCodes && !params.vehicleCodes.length > 0) {
      errMsg.push(ONE_MEDIA_VEHICLE);
      errMsg.push(ONE_DIMENSION);
    } else if (params.vehicleCodes && !params.vehicleCodes.length > 0) {
      errMsg.push(ONE_MEDIA_VEHICLE);
    } else if (params.scoreList && !params.scoreList.length > 0) {
      errMsg.push(ONE_DIMENSION);
    } else if (mmeStudy.ss === "MRI" && noAdStreaming.length && adStatement.length) {
      errMsg.push(DESELECT_NO_AD_STREAMING);
      errMsg.push(DESELECT_GA_STATEMENT);
      isStreamingSpecificError = true;
    } else if (params.statementNames.indexOf('G') < 0 &&
               params.statementNames.indexOf('M') >= 0 &&
               params.vehicleNames.indexOf('M') < 0 &&
              (params.vehicleNames.indexOf('T') >= 0 || params.vehicleNames.indexOf('I') >= 0)) {
      errMsg.push(DESELECT_TV_WEBSITE);
      errMsg.push(ONE_MAGAZINE);
      isMediaSpecificError = true;
    } else if (params.statementNames.indexOf('G') < 0 &&
               params.statementNames.indexOf('M') >= 0 &&
               params.vehicleNames.indexOf('M') >= 0 &&
              (params.vehicleNames.indexOf('T') >= 0 || params.vehicleNames.indexOf('I') >= 0)) {
      errMsg.push(DESELECT_TV_WEBSITE);
      isMediaSpecificError = true;
    } else if (params.statementNames.indexOf('G') < 0 &&
               params.statementNames.indexOf('I') >= 0 &&
               params.vehicleNames.indexOf('I') < 0 &&
              (params.vehicleNames.indexOf('T') >= 0 || params.vehicleNames.indexOf('M') >= 0)) {
      errMsg.push(DESELECT_TV_MAGAZINE);
      errMsg.push(ONE_WEBSITE);
      isMediaSpecificError = true;
    } else if (params.statementNames.indexOf('G') < 0 &&
              params.statementNames.indexOf('I') >= 0 &&
              params.vehicleNames.indexOf('I') >= 0 &&
              (params.vehicleNames.indexOf('T') >= 0 || params.vehicleNames.indexOf('M') >= 0)) {
      errMsg.push(DESELECT_TV_MAGAZINE);
      isMediaSpecificError = true;
    } else if (params.statementNames.indexOf('G') < 0 &&
               params.statementNames.indexOf('T') >= 0 &&
               params.vehicleNames.indexOf('T') < 0 &&
              (params.vehicleNames.indexOf('I') >= 0 || params.vehicleNames.indexOf('M') >= 0)) {
      errMsg.push(DESELECT_WEBSITE_MAGAZINE);
      errMsg.push(ONE_TELEVISION);
      isMediaSpecificError = true;
    } else if (params.statementNames.indexOf('G') < 0 &&
               params.statementNames.indexOf('T') >= 0 &&
               params.vehicleNames.indexOf('T') >= 0 &&
              (params.vehicleNames.indexOf('I') >= 0 || params.vehicleNames.indexOf('M') >= 0)) {
      errMsg.push(DESELECT_WEBSITE_MAGAZINE);
      isMediaSpecificError = true;
    } else {
      errMsg = [];
    }
  }
  const validationMsg = {};
  validationMsg.errMsg = errMsg;
  validationMsg.isMediaSpecificError = isMediaSpecificError;
  validationMsg.isStreamingSpecificError = isStreamingSpecificError;
  return validationMsg;
};
